import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './common/SafeIcon';

const ACCENT_COLORS = [
  { hex: '#EDC1B6', twText: 'text-[#EDC1B6]', twBg: 'bg-[#EDC1B6]', twBorder: 'border-[#EDC1B6]' }, // 0: Chilean Pink (Medical, Community)
  { hex: '#C37A53', twText: 'text-[#C37A53]', twBg: 'bg-[#C37A53]', twBorder: 'border-[#C37A53]' }, // 1: Liqueur Brass (Life, Learning)
  { hex: '#5288BD', twText: 'text-[#5288BD]', twBg: 'bg-[#5288BD]', twBorder: 'border-[#5288BD]' }, // 2: Danube (Perks, Time off)
  { hex: '#99687C', twText: 'text-[#99687C]', twBg: 'bg-[#99687C]', twBorder: 'border-[#99687C]' }, // 3: Raspberry (Hybrid)
];

// Consistent colorIndex mapping across regions:
// Medical (Activity): 0
// Life/Insurance (Shield): 1
// Perks (Gift): 2
// Hybrid (Home): 3
// Time off (Calendar): 2
// Learning (BookOpen): 1
// Community (Users): 0

const benefitsData = {
  singapore: [
    { 
      id: 'sg-1', value: 'S$300', label: 'Medical (Mednefits)', tag: 'Full-time', icon: 'Activity', colorIndex: 0,
      details: 'Comprehensive outpatient medical coverage via the Mednefits platform. You receive a S$300 annual allowance that can be flexibly used for GP visits, dental care, optical needs, and wellness services. Enjoy cashless direct billing at panel clinics.'
    },
    { 
      id: 'sg-2', value: 'S$40,000', label: 'Group life (NTUC GIFT)', tag: 'Full-time', icon: 'Shield', colorIndex: 1,
      details: 'Automatic enrollment into the NTUC GIFT group insurance policy. This provides 24/7 worldwide coverage against death and total & permanent disability (TPD) up to S$40,000, ensuring peace of mind for you and your loved ones.'
    },
    { 
      id: 'sg-3', value: 'NTUC Union Membership', label: 'Perks & Care Fund', tag: 'Full-time', icon: 'Gift', colorIndex: 2,
      details: 'Fully sponsored NTUC union membership. Gain access to the NTUC Care Fund (which includes support like e-vouchers for daily essentials during hardship), earn Linkpoints on groceries, and enjoy exclusive lifestyle and dining privileges.'
    },
    { 
      id: 'sg-4', value: 'Hybrid', label: 'Flexible & hybrid work', tag: 'FT & contract', icon: 'Home', colorIndex: 3,
      details: 'We believe in work-life integration. Enjoy a flexible, hybrid working arrangement with a tailored mix of remote focus days and in-office collaboration days, designed to suit both your role and personal needs.'
    },
    { 
      id: 'sg-5', value: 'Birthday+', label: 'Time off', tag: 'FT & contract', icon: 'Calendar', colorIndex: 2,
      details: 'A generous time-off package to help you recharge. This includes your standard annual leave, a dedicated paid day off for your birthday, company-wide year-end closure days, and a paid volunteer day to give back to the community.'
    },
    { 
      id: 'sg-6', value: 'S$500/yr', label: 'Learning (NTUC UTAP)', tag: 'Full-time', icon: 'BookOpen', colorIndex: 1,
      details: 'Union Training Assistance Programme (UTAP) supports your continuous upskilling. Enjoy 50% unfunded course fee support, capped at up to S$500/year for members aged 40 and above, and up to S$250/year for members under 40. You can also claim Class 3 and Class 2 driving licenses as part of this learning package.'
    },
    { 
      id: 'sg-7', value: 'Cohesion Dept Credits', label: 'Team & culture', tag: 'Everyone', icon: 'Users', colorIndex: 0,
      details: 'Be part of a vibrant, inclusive workplace. We foster connections through regular team-building activities, festive celebrations, a well-stocked pantry, and a highly collaborative open-door culture. Additionally, each personnel receives S$100 in cohesion department credits per year for team bonding and social activities.'
    },
  ],
  malaysia: [
    { 
      id: 'my-1', value: 'RM750', label: 'Medical (Mednefits, incl. optical)', tag: 'Full-time', icon: 'Activity', colorIndex: 0,
      details: 'RM750 annual medical allowance managed via the Mednefits app. Can be utilized seamlessly across a wide network of clinics for outpatient GP visits, dental procedures, optical needs, and wellness programs.'
    },
    { 
      id: 'my-2', value: 'Hybrid', label: 'Flexible & hybrid work', tag: 'FT & contract', icon: 'Home', colorIndex: 3,
      details: 'Enjoy flexible working hours and a remote-friendly hybrid schedule. We prioritize outcomes over face-time, allowing you to balance office collaboration with focused remote work.'
    },
    { 
      id: 'my-3', value: 'Birthday+', label: 'Time off', tag: 'FT & contract', icon: 'Calendar', colorIndex: 2,
      details: 'Comprehensive leave entitlement to ensure you stay rested. Alongside standard annual leave and public holidays, enjoy a special paid day off just for your birthday and dedicated festive breaks.'
    },
    { 
      id: 'my-4', value: 'Supported', label: 'Learning & growth', tag: 'All crew', icon: 'BookOpen', colorIndex: 1,
      details: 'We invest in your potential. Access continuous learning opportunities, internal workshops, and professional growth resources to advance your career trajectory with us.'
    },
    { 
      id: 'my-5', value: 'Cohesion Dept Credits', label: 'Team & culture', tag: 'Everyone', icon: 'Users', colorIndex: 0,
      details: 'Join a tight-knit, collaborative local team. We regularly organize social events, company lunches, and team-building activities to maintain a vibrant and supportive workplace environment. Additionally, each personnel receives RM 300 per pax in cohesion department credits per year for team bonding and social activities.'
    },
  ],
  indonesia: [
    { 
      id: 'id-1', value: 'Hybrid', label: 'Flexible & hybrid work', tag: 'FT & contract', icon: 'Home', colorIndex: 3,
      details: 'A flexible, outcome-driven work policy. We empower you to balance remote work for deep focus with strategic in-person days for team alignment and collaboration.'
    },
    { 
      id: 'id-2', value: 'Birthday+', label: 'Time off', tag: 'FT & contract', icon: 'Calendar', colorIndex: 2,
      details: 'Generous time off to maintain well-being. Includes your standard annual leave quotas, national public holidays, plus a dedicated day off to celebrate your birthday.'
    }
  ]
};

const BenefitCard = ({ data, index, onClick }) => {
  // Using consistent color mapping based on the benefit type instead of array position
  const accent = ACCENT_COLORS[data.colorIndex % ACCENT_COLORS.length];

  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3, delay: index * 0.05, ease: [0.23, 1, 0.32, 1] }}
      className="group relative flex flex-col justify-between overflow-hidden rounded-xl bg-white border border-gray-100 p-4 shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_8px_20px_rgb(0,0,0,0.08)] transition-all duration-300 min-h-[110px] text-left cursor-pointer w-full focus:outline-none focus:ring-2 focus:ring-vicinity-pink/30"
    >
      {/* Decorative gradient blob on hover */}
      <div 
        className="absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-0 blur-[20px] transition-opacity duration-500 group-hover:opacity-15 pointer-events-none" 
        style={{ backgroundColor: accent.hex }}
      />

      <div className="flex items-start justify-between mb-2 w-full">
        <SafeIcon name={data.icon} className={`text-lg ${accent.twText} opacity-90`} />
        {data.tag && (
          <span className="text-[9px] md:text-[10px] font-bold tracking-wider text-vicinity-text opacity-40 uppercase bg-[#F8F9FA] px-2 py-0.5 rounded-full border border-gray-100">
            {data.tag}
          </span>
        )}
      </div>

      <div className="mt-auto relative z-10 w-full">
        {/* Adjusted text sizing and line-height slightly to accommodate longer strings like "NTUC Union Membership" */}
        <div className={`text-lg md:text-xl font-bold tracking-tight mb-1 leading-tight ${accent.twText}`}>
          {data.value}
        </div>
        <div className="text-xs text-vicinity-text opacity-80 leading-snug flex justify-between items-center group-hover:text-vicinity-text transition-colors">
          <span className="pr-2">{data.label}</span>
          <SafeIcon name="ChevronRight" className="text-vicinity-text opacity-0 -translate-x-2 group-hover:opacity-30 group-hover:translate-x-0 transition-all duration-300 w-3 h-3 flex-shrink-0" />
        </div>
      </div>
    </motion.button>
  );
};

const BenefitModal = ({ selected, onClose }) => {
  if (!selected) return null;
  const { data } = selected;
  const accent = ACCENT_COLORS[data.colorIndex % ACCENT_COLORS.length];

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center p-4 md:p-6 overflow-hidden">
      {/* Backdrop */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        className="absolute inset-0 bg-vicinity-text/30 backdrop-blur-sm cursor-pointer"
      />
      
      {/* Modal Card */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header Strip */}
        <div className={`h-2 w-full ${accent.twBg}`} />
        
        <div className="p-6 md:p-8 flex-1 overflow-y-auto custom-scrollbar">
          <button 
            onClick={onClose}
            className="absolute top-6 right-6 p-1.5 rounded-full bg-gray-50 text-vicinity-text/50 hover:bg-gray-100 hover:text-vicinity-text transition-colors"
          >
            <SafeIcon name="X" className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2.5 rounded-xl bg-gray-50 ${accent.twText}`}>
              <SafeIcon name={data.icon} className="w-6 h-6" />
            </div>
            {data.tag && (
              <span className="text-[10px] font-bold tracking-wider text-vicinity-text opacity-50 uppercase bg-gray-100 px-2.5 py-1 rounded-full">
                {data.tag}
              </span>
            )}
          </div>

          <h3 className={`text-2xl md:text-3xl font-bold tracking-tight mb-2 ${accent.twText}`}>
            {data.value}
          </h3>
          <h4 className="text-sm font-bold text-vicinity-text uppercase tracking-wide mb-4 pb-4 border-b border-gray-100">
            {data.label}
          </h4>
          
          <div className="text-sm md:text-[15px] text-vicinity-text/80 leading-relaxed space-y-4">
            <p>{data.details}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default function App() {
  const [activeRegion, setActiveRegion] = useState('singapore');
  const [selectedBenefit, setSelectedBenefit] = useState(null);

  return (
    // Fixed container matching approx 16:9 1180x660 visually, highly responsive
    <div className="w-full h-full min-h-[100dvh] md:min-h-[660px] relative z-0 selection:bg-vicinity-pink/30 selection:text-vicinity-text flex flex-col justify-center overflow-hidden bg-vicinity-bg">
      
      {/* Brand Background Gradient Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-[#EDC1B6] opacity-30 blur-[100px]" />
        <div className="absolute top-[20%] right-[-10%] w-[40%] h-[60%] rounded-full bg-[#C37A53] opacity-15 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[20%] w-[50%] h-[50%] rounded-full bg-[#5288BD] opacity-20 blur-[100px]" />
        <div className="absolute bottom-[10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-[#99687C] opacity-20 blur-[100px]" />
      </div>

      <div className="max-w-[1180px] w-full mx-auto px-4 py-8 md:px-8 flex flex-col relative h-full justify-center">
        
        {/* SLIM HEADER ROW */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end w-full mb-6 gap-4">
          <div>
            <span className="text-[#5288BD] text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase block mb-1">
              VICINITY
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-vicinity-text tracking-tight leading-none">
              Benefits & Wellbeing
            </h2>
          </div>

          {/* Region Toggle Pills */}
          <div className="inline-flex items-center p-1 bg-white rounded-full border border-gray-200 shadow-sm">
            {['singapore', 'malaysia', 'indonesia'].map((region) => (
              <button
                key={region}
                onClick={() => {
                  setActiveRegion(region);
                  setSelectedBenefit(null);
                }}
                className={`relative px-4 py-1.5 text-[11px] md:text-xs font-bold rounded-full transition-colors duration-300 whitespace-nowrap ${
                  activeRegion === region ? 'text-vicinity-text' : 'text-vicinity-text/50 hover:text-vicinity-text/80'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {activeRegion === region && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-[#F3F3F3] border border-gray-200 rounded-full shadow-inner"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <span className="relative z-10 capitalize">{region}</span>
              </button>
            ))}
          </div>
        </div>

        {/* COMPACT GRID */}
        <div className="flex-none relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeRegion}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col"
            >
              <div className="grid gap-3 md:gap-4 grid-cols-2 md:grid-cols-4">
                {benefitsData[activeRegion].map((benefit, index) => (
                  <BenefitCard 
                    key={benefit.id} 
                    data={benefit} 
                    index={index} 
                    onClick={() => setSelectedBenefit({ data: benefit, index })}
                  />
                ))}
              </div>
              
              {/* Indonesia-specific note */}
              {activeRegion === 'indonesia' && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mt-4"
                >
                  <p className="text-[10px] md:text-xs text-vicinity-text opacity-70 font-medium inline-block px-3 py-1.5 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200/60 shadow-sm">
                    Medical & region-specific benefits confirmed during hiring.
                  </p>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* FOOTNOTE */}
        <div className="mt-6 pt-4 border-t border-gray-200/60 w-full flex-none">
          <p className="text-[10px] md:text-[11px] text-vicinity-text opacity-60 font-medium leading-relaxed">
            Eligible full-time employees; contract crew get the same, excluding Mednefits & union (NTUC) benefits. Confirmed on joining. Click tiles for details. Note: Benefits change annually.
          </p>
        </div>

      </div>

      {/* MODAL PORTAL (Overlayed on the entire widget) */}
      <AnimatePresence>
        {selectedBenefit && (
          <BenefitModal 
            selected={selectedBenefit} 
            onClose={() => setSelectedBenefit(null)} 
          />
        )}
      </AnimatePresence>

    </div>
  );
}